import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { api } from '../utils/api';
import { devLog } from '../utils/devTools';

interface AuthStatusResponse { success: boolean; rsrcNo?: string; email?: string; schedulingEnabled?: boolean; isAgency?: boolean; agencyLoginEnabled?: boolean; individualEmail?: string; }
interface AuthMessageResponse { success: boolean; message?: string; }
interface AuthVerifyResponse { success: boolean; rsrcNo?: string; schedulingEnabled?: boolean; isAgency?: boolean; agencyLoginEnabled?: boolean; message?: string; }

interface AuthContextType {
  isAuthenticated: boolean;
  rsrcNo: string | null;
  email: string;
  setEmail: React.Dispatch<React.SetStateAction<string>>;
  loading: boolean;
  error: string | null;
  token: string;
  emailSubmitted: boolean;
  setEmailSubmitted: React.Dispatch<React.SetStateAction<boolean>>;
  schedulingEnabled: boolean;
  isAgency: boolean;
  agencyLoginEnabled: boolean;
  passkeyAvailable: boolean;
  individualEmail: string | null;
  loginWithEmail: (email: string, turnstileToken: string) => Promise<{ success: boolean; message: string }>;
  loginWithPasskey: (email: string) => Promise<{ success: boolean; message: string }>;
  verifyToken: (token: string) => Promise<{ success: boolean; message: string }>;
  resendToken: () => Promise<{ success: boolean; message: string }>;
  logout: () => void;
  reloadAuthStatus: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false);
  const [rsrcNo, setRsrcNo] = useState<string | null>(null);
  const [email, setEmail] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [token, setToken] = useState<string>('');
  const [emailSubmitted, setEmailSubmitted] = useState<boolean>(false);
  const [schedulingEnabled, setSchedulingEnabled] = useState<boolean>(false);
  const [isAgency, setIsAgency] = useState<boolean>(false);
  const [agencyLoginEnabled, setAgencyLoginEnabled] = useState<boolean>(false);
  const [passkeyAvailable, setPasskeyAvailable] = useState<boolean>(false);
  const [individualEmail, setIndividualEmail] = useState<string | null>(null);

  // Check if passkeys are supported in this browser
  useEffect(() => {
    import('../services/PasskeyService').then(({ PasskeyService }) => {
      const available = PasskeyService.isAvailable();
      devLog('[DEBUG AuthContext] Passkey availability:', available);
      setPasskeyAvailable(available);
    });
  }, []);

  const checkAuthStatus = async () => {
    try {
      devLog("[DEBUG] Checking auth status...");
      const response = await api.get<AuthStatusResponse>('/api/auth/status');
      devLog("[DEBUG] Auth status response:", response);
      if (response.success) {
        setIsAuthenticated(true);
        setRsrcNo(response.rsrcNo ?? null);
        if (response.email) setEmail(response.email);
        setSchedulingEnabled(response.schedulingEnabled || false);
        setIsAgency(response.isAgency ?? false);
        setAgencyLoginEnabled(response.agencyLoginEnabled ?? false);
        setIndividualEmail(response.individualEmail ?? null);
      } else {
        setIsAuthenticated(false);
        setRsrcNo(null);
        setSchedulingEnabled(false);
        setIsAgency(false);
        setAgencyLoginEnabled(false);
        setIndividualEmail(null);
      }
    } catch (error) {
      console.error("[DEBUG] Auth status error:", error);
      setIsAuthenticated(false);
      setRsrcNo(null);
      setSchedulingEnabled(false);
      setIsAgency(false);
      setAgencyLoginEnabled(false);
      setIndividualEmail(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    checkAuthStatus();
  }, []);

  const loginWithEmail = async (email: string, turnstileToken: string): Promise<{ success: boolean; message: string }> => {
    setLoading(true);
    setError(null);
    try {
      const response = await api.post<AuthMessageResponse>('/api/auth/login', {
        email,
        turnstileToken // Include the Turnstile token in the request
      });
      if (response.success) {
        setEmail(email);
        // Set emailSubmitted to true when login is successful
        setEmailSubmitted(true);
        return { success: true, message: response.message || 'Check your email for the verification token' };
      } else {
        setError(response.message || 'Email not found');
        return { success: false, message: response.message || 'Email not found' };
      }
    } catch (error) {
      const errorMessage = 'An error occurred during login';
      setError(errorMessage);
      return { success: false, message: errorMessage };
    } finally {
      setLoading(false);
    }
  };

  const verifyToken = async (token: string): Promise<{ success: boolean; message: string }> => {
    setLoading(true);
    setError(null);
    try {
      const response = await api.post<AuthVerifyResponse>('/api/auth/verify', { email, token });
      if (response.success) {
        setIsAuthenticated(true);
        setRsrcNo(response.rsrcNo ?? null);
        setSchedulingEnabled(response.schedulingEnabled || false);
        setIsAgency(response.isAgency ?? false);
        setAgencyLoginEnabled(response.agencyLoginEnabled ?? false);
        setToken('');
        // Reset emailSubmitted on successful verification
        setEmailSubmitted(false);
        return { success: true, message: response.message || 'Successfully authenticated' };
      } else {
        setError(response.message || 'Invalid token');
        return { success: false, message: response.message || 'Invalid token' };
      }
    } catch (error) {
      const errorMessage = 'An error occurred during verification';
      setError(errorMessage);
      return { success: false, message: errorMessage };
    } finally {
      setLoading(false);
    }
  };

  const resendToken = async (): Promise<{ success: boolean; message: string }> => {
    setLoading(true);
    setError(null);
    try {
      const response = await api.post<AuthMessageResponse>('/api/auth/resend', { email });
      if (response.success) {
        return { success: true, message: response.message || 'Token resent successfully' };
      } else {
        setError(response.message || 'Failed to resend token');
        return { success: false, message: response.message || 'Failed to resend token' };
      }
    } catch (error) {
      const errorMessage = 'An error occurred when resending token';
      setError(errorMessage);
      return { success: false, message: errorMessage };
    } finally {
      setLoading(false);
    }
  };

  const loginWithPasskey = async (email: string): Promise<{ success: boolean; message: string }> => {
    setLoading(true);
    setError(null);
    try {
      const { PasskeyService } = await import('../services/PasskeyService');
      const result = await PasskeyService.authenticateWithPasskey(email);

      if (result.success) {
        // Re-check auth status to update context
        const response = await api.get<AuthStatusResponse>('/api/auth/status');
        if (response.success) {
          setIsAuthenticated(true);
          setRsrcNo(response.rsrcNo ?? null);
          setSchedulingEnabled(response.schedulingEnabled || false);
          setIsAgency(response.isAgency ?? false);
          setAgencyLoginEnabled(response.agencyLoginEnabled ?? false);
          setIndividualEmail(response.individualEmail ?? null);
        }
      } else {
        setError(result.message);
      }

      return result;
    } catch (error) {
      const errorMessage = 'An error occurred during passkey authentication';
      setError(errorMessage);
      return { success: false, message: errorMessage };
    } finally {
      setLoading(false);
    }
  };

  const logout = () => {
    api.post('/api/auth/logout', {}) // Send empty object as request body
      .then(() => {
        setIsAuthenticated(false);
        setRsrcNo(null);
        setEmail('');
        setEmailSubmitted(false);
        setSchedulingEnabled(false);
        // Navigate to login page
        window.location.href = '/login';
      })
      .catch(() => {
        // Even if server logout fails, clear client state
        setIsAuthenticated(false);
        setRsrcNo(null);
        setEmail('');
        setEmailSubmitted(false);
        setSchedulingEnabled(false);
      });
  };

  return (
    <AuthContext.Provider
      value={{
        isAuthenticated,
        rsrcNo,
        email,
        setEmail,
        loading,
        error,
        token,
        emailSubmitted,
        setEmailSubmitted,
        schedulingEnabled,
        isAgency,
        agencyLoginEnabled,
        passkeyAvailable,
        individualEmail,
        loginWithEmail,
        loginWithPasskey,
        verifyToken,
        resendToken,
        logout,
        reloadAuthStatus: checkAuthStatus,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};